import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import type { DateRange } from 'moment-range';
import { Subject } from 'rxjs';

import {
  ChartTypes,
  Company,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  Form,
  Project,
  Team,
  Template,
} from '@site-mate/dashpivot-shared-library';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { Dashboard } from 'app/analytics/dashboards/dashboard';
import { TemplatesService } from 'app/apps/templates.service';
import { SegmentService } from 'app/segment/segment.service';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';
import { ConfirmService, confirmExitOperations } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-chart-view-modal',
  templateUrl: './chart-view-modal.component.html',
  styleUrls: ['./chart-view-modal.component.scss'],
})
export class ChartViewModalComponent implements OnInit, OnDestroy {
  readonly chartTypes = ChartTypes;
  readonly learnMoreLinks = {
    [ChartTypes.Quantity]: 'https://intercom.help/dashpivot/en/articles/4681587-quantity-charts',
    [ChartTypes.Frequency]: 'https://intercom.help/dashpivot/en/articles/4681584-frequency-charts',
    [ChartTypes.Ratio]: 'https://intercom.help/dashpivot/en/articles/4681583-ratio-charts',
  };

  @Input() teamId: string;
  @Input() companyId: string;
  @Input() dashboardId: string;
  @Input() chartId: string;
  @Input() company: Company;
  @Input() project: Project;
  @Input() team: Team;
  @Input() dashboard: Dashboard;

  @ViewChild('editName') editNameElement: ElementRef;

  chart: ChartSettings;
  unSubscribe = new Subject<void>();

  templates: Template[] = [];

  editNameVisible = false;
  displayedChartName: string;
  canDelete: boolean;
  isChartSetup = false;

  forms: Form[] = [];
  dataDateRange: DateRange;

  loading: boolean;

  chartTemplate;
  chartFilters;
  chartTimeframe;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly chartUtilsService: ChartUtilsService,
    private readonly analyticsService: AnalyticsService,
    private readonly templatesService: TemplatesService,
    private readonly confirmService: ConfirmService,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
    private readonly segmentService: SegmentService,
    private readonly userService: UserService,
  ) {}

  async ngOnInit() {
    try {
      this.loading = true;
      if (this.teamId) {
        await this.initForTeam();
      }

      if (this.companyId) {
        await this.initForCompany();
      }

      this.captureState(this.chart);
      this.addSampleTemplate();
      this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);
    } catch (error) {
      this.errorHandler.handle(error);
    } finally {
      this.loading = false;
    }
  }

  private async initForTeam() {
    const chart = await this.analyticsService
      .getTeamChart(this.teamId, this.dashboardId, this.chartId)
      .toPromise();

    this.templates = await this.templatesService
      .getTeamAppsForMakingChart(this.teamId, chart.kind)
      .toPromise();

    this.chart = chart;

    if (!this.chart.isSampleChart && !this.templates.length) {
      this.toastrService.warnByKey('needTemplateBeforeCreateChart');
      await this.analyticsService.deleteTeamChart(this.teamId, this.dashboardId, this.chartId).toPromise();
      this.activeModal.close();
      return;
    }

    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ChartModalViewed, {
        Hierarchy: 'Team',
        Context: this.chart.kind,
      }),
      this.segmentService,
    );
  }

  private async initForCompany() {
    const chart = await this.analyticsService
      .getCompanyChart(this.companyId, this.dashboardId, this.chartId)
      .toPromise();

    this.templates = await this.templatesService
      .getCompanyAppsForMakingChart(this.companyId, chart.kind)
      .toPromise();

    this.chart = chart;

    if (!this.chart.isSampleChart && !this.templates.length) {
      this.toastrService.warnByKey('needTemplateBeforeCreateChart');
      await this.analyticsService
        .deleteCompanyChart(this.companyId, this.dashboardId, this.chartId)
        .toPromise();
      this.activeModal.close();
      return;
    }

    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ChartModalViewed, {
        Hierarchy: 'Organisation',
        Context: this.chart.kind,
      }),
      this.segmentService,
    );
  }

  get filepath() {
    const companyName = this.company ? this.company.name : '';
    const projectName = this.project ? `/${this.project.name}` : '';
    const teamName = this.team ? `/${this.team.name}` : '';
    return `${companyName}${projectName}${teamName}`;
  }

  async onFilterChange(chart: ChartSettings) {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ChartFilterApplied, {
        Context: this.chart.kind,
      }),
      this.segmentService,
    );

    await this.onSettingsChange(chart);
  }

  async onSettingsChange(chart: ChartSettings) {
    try {
      this.loading = true;
      this.isChartSetup = this.chartUtilsService.isChartSetup(chart);

      if (this.teamId) {
        this.chart = await this.analyticsService
          .updateTeamChart(this.teamId, this.dashboardId, this.chartId, chart)
          .toPromise();
      }

      if (this.companyId) {
        this.chart = await this.analyticsService
          .updateCompanyChart(this.companyId, this.dashboardId, this.chartId, chart)
          .toPromise();
      }

      if (this.shouldRefreshData(this.chart)) {
        this.forms = await this.analyticsService
          .listTemplateForms(chart.templateId, this.chartUtilsService.getURLParams(chart))
          .toPromise();
      }

      this.captureState(this.chart);

      this.dataDateRange = this.chartUtilsService.getRange(this.chart, this.forms);

      this.addSampleTemplate();

      this.displayedChartName = this.chartUtilsService.getChartName(this.chart);
    } catch (error) {
      this.errorHandler.handle(error);
    } finally {
      this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);
      this.loading = false;
    }
  }

  private addSampleTemplate() {
    if (this.chart?.isSampleChart && !this.templates.includes(this.chart.template)) {
      this.templates = [this.chart.template];
    }
  }

  private captureState(chart: ChartSettings) {
    this.chartFilters = JSON.stringify(chart.filters);
    this.chartTimeframe = chart.timeframe;
    this.chartTemplate = chart.templateId;
  }

  private shouldRefreshData(chart: ChartSettings): boolean {
    return (
      !!chart.templateId &&
      (!this.forms?.length ||
        chart.templateId !== this.chartTemplate ||
        chart.timeframe !== this.chartTimeframe ||
        JSON.stringify(chart.filters) !== this.chartFilters)
    );
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  showEditName() {
    this.editNameVisible = true;
    this.chart.name = this.chart.name || this.chartUtilsService.getChartName(this.chart);

    // the focus can be set only after the element is visible
    // so we need to execute it in the next cycle
    setTimeout(() => {
      this.editNameElement.nativeElement.focus();
    }, 0);
  }

  saveName() {
    this.editNameVisible = false;

    if (!this.chart.name) {
      delete this.chart.name;
    }

    void this.onSettingsChange(this.chart);
  }

  getTemplateSortingFieldName(template: Template) {
    const sortField = template?.items.find(
      (item) => template.sortFormsBy[0] && template.sortFormsBy[0] === item._id,
    );
    return sortField ? sortField.description : 'Creation date';
  }

  async onDelete() {
    try {
      await this.confirmService.confirmDelete('deleteCharts');
      if (this.teamId) {
        await this.analyticsService.deleteTeamChart(this.teamId, this.dashboardId, this.chartId).toPromise();
      }

      if (this.companyId) {
        await this.analyticsService
          .deleteCompanyChart(this.companyId, this.dashboardId, this.chartId)
          .toPromise();
      }
      this.onClose();
    } catch (error) {
      if (!confirmExitOperations.includes(error as string)) {
        this.errorHandler.handle(error);
      }
    }
  }
}
